import { Button, Typography } from '@mui/material';
import clsx from 'clsx';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLayoutsButtonStyles from './styles';
import { ScreenLayoutModelProps } from './types';

import {
  LayoutInput,
  ScreenFragment,
  ScreenInput,
  ScreenType,
  useSaveScreenMutation,
  useSetActiveHardwareScreenMutation,
} from '@kinetic-ui/data-layer';
import Dialog from 'components/Dialog';
import { useGraphLoading } from 'kineticUtils';
import { LayoutArea } from '../LayoutArea';
import { LAYOUT_ICON_PROPS } from './constants';

const ScreenLayoutsModel: React.FunctionComponent<ScreenLayoutModelProps> = (props) => {
  const { t } = useTranslation('video');
  const { screen, showDialog, setShowDialog, children, cameraName } = props;
  const [selectedArea, setSelectedArea] = useState<number | null>(null);
  const classes = useLayoutsButtonStyles(props);
  const [setActiveHardware] = useSetActiveHardwareScreenMutation();
  const onCancel = useCallback(() => {
    setShowDialog(false);
  }, [setShowDialog]);

  const saveResultMessages = useMemo(() => {
    return {
      success: t('common:successfulSave', { name: t('video:screen') }),
      error: t('common:errorOnSave', { name: t('video:screen') }),
    };
  }, [t]);
  const [saveScreen, result] = useSaveScreenMutation({
    onCompleted: () => {
      setShowDialog(false);
      const { data } = result;
      if (data?.saveScreen?.type === ScreenType.Hardware) {
        setActiveHardware({ variables: { id: data.saveScreen.id } });
      }
    },
  });

  const createScreenInput = useCallback((screen: ScreenFragment, updateContent) => {
    return {
      id: screen?.id!,
      name: screen?.name!,
      layout: {
        id: screen?.layout.id!,
        name: screen?.layout.name!,
        description: screen?.layout.description!,
        areas: screen?.layout.areas.map((area) => ({
          columns: area.columns!,
          rows: area.rows!,
          areaId: area.areaId,
          disabled: area.disabled,
        }))!,
      },
      type: screen?.type,
      videoWallId: screen?.videoWallId,
      orderNum: screen?.orderNum!,
      ownershipType: screen?.ownershipType!,
      content: [...updateContent!],
    };
  }, []);

  useGraphLoading({ result, name: 'saveScreen', messages: saveResultMessages });
  const saveScreenCamera = useCallback(() => {
    const content = screen?.content!;
    if (content.length > 0 && selectedArea !== null) {
      const newSelectionId = cameraName?.id ?? null;
      const seenIds = new Set(newSelectionId ? [newSelectionId] : []);

      const isFilled = content[selectedArea] ?? null;
      const selectedIndex = newSelectionId
        ? content.findIndex((data) => data?.id === newSelectionId)
        : -1;
      const updateContent = content.map((data, index) => {
        const currentId = index === selectedArea ? newSelectionId : data?.id;
        if (!currentId) {
          return {};
        }
        if (isFilled && selectedIndex === index) {
          return { camera: isFilled.id };
        }
        if (index === selectedArea) {
          return { camera: currentId };
        }
        if (seenIds.has(currentId)) {
          return {};
        }
        seenIds.add(currentId);
        return { camera: currentId };
      });
      const screenInput: ScreenInput = createScreenInput(screen!, updateContent);
      saveScreen({
        variables: {
          input: {
            ...screenInput,
          },
        },
      });
    }
  }, [screen, selectedArea, createScreenInput, saveScreen, cameraName?.id]);

  const renderButtons = () => {
    return (
      <>
        <Button onClick={onCancel}>{t('cancel')}</Button>
        <Button
          onClick={saveScreenCamera}
          disabled={result.loading}
          color='primary'
          variant='contained'
        >
          {t('apply')}
        </Button>
      </>
    );
  };

  const renderLayoutAreas = useCallback(
    (selectedLayout: LayoutInput) => {
      const { areas } = selectedLayout;
      return areas.map((area, index) => (
        <LayoutArea
          key={`layoutArea_${area.columns}${area.rows}${index}`}
          layoutIconProps={LAYOUT_ICON_PROPS}
          area={area}
          index={index}
          onClick={() => {
            setSelectedArea(index);
          }}
          selectedArea={selectedArea!}
        >
          {children && (children as ReactNode[])[index]}
        </LayoutArea>
      ));
    },
    [children, selectedArea]
  );

  return (
    <Dialog
      open={showDialog}
      title={`${screen?.name}`}
      buttons={renderButtons()}
      containerClassName={classes.containerClassName}
    >
      <>
        <Typography variant='caption'>{cameraName?.name}</Typography>
        <Typography variant='body1'>
          {t('selectSlotApplyThisCameraTo', { screen: `${screen?.name}` })}
        </Typography>
        <div className={classes.gridLayoutsContainer}>
          {screen?.layout && (
            <div className={clsx(classes.layoutIconWrapper)}>
              <div className={classes.container}>{renderLayoutAreas(screen?.layout)}</div>
            </div>
          )}
        </div>
      </>
    </Dialog>
  );
};

export default ScreenLayoutsModel;
